import { cn } from "@/lib/utils";

import {
  CHIP_FILLED_SPEC,
  CHIP_LABEL,
  CHIP_OUTLINED_SECONDARY,
  CHIP_OUTLINED_SPEC,
  CHIP_ROOT,
  CHIP_ROOT_SMALL,
} from "./chip-shape";

// The donor's `SpecificationChip` (`src/components/specification-chip/
// specification-chip.tsx:5-18`): a small MUI `Chip`, `px: 0.5` (4px on the
// root), `data-testid='specification-chip'` — **filled when its label is "Core
// Specification", outlined otherwise**, at the theme's `chipColor` unless the
// caller passes another. The details page's metadata line shows Core or Base
// and nothing for a Community spec (`SpecMetadata.tsx:73-86`); the edit header
// shows the same pair and a grey outlined "Draft" at `secondaryChipColor`
// (`EditSpecChips.tsx:50-58`).
//
// A `kind` rather than the donor's free `label`: the label decides the variant
// in the source, so taking the label would mean re-deriving the rule from a
// string, and the four readings are the whole of what any caller shows.

const KINDS = {
  core: { label: "Core Specification", paint: CHIP_FILLED_SPEC, labelPadding: "px-2" },
  base: { label: "Base Specification", paint: CHIP_OUTLINED_SPEC, labelPadding: "px-[7px]" },
  draft: { label: "Draft", paint: CHIP_OUTLINED_SECONDARY, labelPadding: "px-[7px]" },
} as const;

interface SpecificationChipProps {
  kind: keyof typeof KINDS | "community";
}

export function SpecificationChip({ kind }: SpecificationChipProps) {
  if (kind === "community") return null;
  const { label, paint, labelPadding } = KINDS[kind];
  return (
    <span
      data-slot="chip"
      data-testid="specification-chip"
      className={cn(CHIP_ROOT, CHIP_ROOT_SMALL, paint, "px-1")}
    >
      <span data-slot="chip-label" className={cn(CHIP_LABEL, "leading-normal", labelPadding)}>
        {label}
      </span>
    </span>
  );
}
